import React, { useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import moment from "moment-timezone";
import { isValidIanaTimezone } from "../../../Utils/DateAndTime";

function timeStrToMinutes(t) {
  if (t === undefined || t === null) return null;
  const s = String(t).trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(mi)) return null;
  return h * 60 + mi;
}

/** Format API time strings (HH:mm / HH:mm:ss) for list display. */
function formatTimeForDisplay(t) {
  const s = String(t ?? "").trim();
  if (!s) return "";
  const parsed = moment(s, ["HH:mm:ss", "HH:mm", "H:mm"], true);
  return parsed.isValid() ? parsed.format("h:mm A") : s.slice(0, 8);
}

/** True if "now" in `tz` falls inside either morning or evening student windows (inclusive). */
export function isWithinLeadContactWindows(
  tz,
  morningStart,
  morningEnd,
  eveningStart,
  eveningEnd
) {
  if (!tz || !isValidIanaTimezone(tz)) return false;
  const now = moment.tz(tz);
  const cur = now.hours() * 60 + now.minutes();
  const inRange = (a, b) => {
    const s = timeStrToMinutes(a);
    const e = timeStrToMinutes(b);
    if (s === null || e === null) return false;
    return cur >= s && cur <= e;
  };
  return (
    inRange(morningStart, morningEnd) ||
    inRange(eveningStart, eveningEnd)
  );
}

/**
 * Leads list: live clock in lead's contact timezone + green dot when inside contact windows.
 */
export default function LeadListContactCell({
  timezone,
  morningStart,
  morningEnd,
  eveningStart,
  eveningEnd,
  /** From `lead_contact_timings` — India Standard Time window starts (API). */
  morningIstStart,
  eveningIstStart,
  showDot = true,
  showTime = true,
  compact = false,
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const tz = String(timezone || "").trim();
  const mIst = String(morningIstStart || "").trim();
  const eIst = String(eveningIstStart || "").trim();
  if (!tz || !isValidIanaTimezone(tz)) {
    if (compact) return null;
    if (mIst || eIst) {
      const line = `Morning IST: ${mIst ? formatTimeForDisplay(mIst) : "—"} · Evening IST: ${eIst ? formatTimeForDisplay(eIst) : "—"}`;
      return (
        <Tooltip title={line} arrow enterDelay={300}>
          <Box
            component="span"
            sx={{
              fontSize: "0.72rem",
              color: "text.secondary",
              lineHeight: 1.35,
              maxWidth: 280,
              wordBreak: "break-word",
            }}
          >
            {line}
          </Box>
        </Tooltip>
      );
    }
    return (
      <Box component="span" sx={{ color: "text.secondary" }}>
        {showTime ? "—" : ""}
      </Box>
    );
  }

  const now = moment.tz(tz);
  const activeNow = isWithinLeadContactWindows(
    tz,
    morningStart,
    morningEnd,
    eveningStart,
    eveningEnd
  );
  const displayTime = now.format("h:mm A");
  const abbrStr = now.format("z");
  const windowsHint = `Windows (local): ${String(morningStart || "?").slice(0, 5)}–${String(morningEnd || "?").slice(0, 5)}, ${String(eveningStart || "?").slice(0, 5)}–${String(eveningEnd || "?").slice(0, 5)}`;
  const istHint =
    mIst || eIst
      ? ` | IST — Morning start: ${mIst ? formatTimeForDisplay(mIst) : "—"} · Evening start: ${eIst ? formatTimeForDisplay(eIst) : "—"}`
      : "";
  const title = activeNow
    ? `Within preferred contact hours (${windowsHint})${istHint}`
    : `Outside contact hours (${windowsHint})${istHint}`;

  const istLine =
    mIst || eIst
      ? `Morning IST: ${mIst ? formatTimeForDisplay(mIst) : "—"} · Evening IST: ${eIst ? formatTimeForDisplay(eIst) : "—"}`
      : "";

  return (
    <Tooltip title={title} arrow enterDelay={300}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.25,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          {showDot ? (
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: activeNow ? "#22c55e" : "action.disabledBackground",
                boxShadow: activeNow ? "0 0 0 1px rgba(34,197,94,0.35)" : "none",
              }}
              aria-hidden
            />
          ) : null}
          {showTime ? (
            <Box
              component="span"
              sx={{
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {displayTime}
              {abbrStr ? (
                <Box
                  component="span"
                  sx={{ color: "text.secondary", ml: 0.5, fontSize: "0.85em" }}
                >
                  {abbrStr}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
        {showTime && istLine && !compact ? (
          <Box
            component="span"
            sx={{
              fontSize: "0.72rem",
              color: "text.secondary",
              lineHeight: 1.35,
              maxWidth: 280,
              wordBreak: "break-word",
            }}
          >
            {istLine}
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
}
